<div class="min-vh-100 bg-light">
  <app-header [page]="'Admin'"></app-header>

  <main class="container py-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <div>
        <h4 class="mb-0 fw-bold">Withdrawal Detail</h4>
        <div class="text-muted small">Inspect and manage a withdrawal request</div>
      </div>

      <button class="btn btn-outline-secondary btn-sm" type="button" (click)="back()">
        Back
      </button>
    </div>

    <div *ngIf="loading" class="card shadow-sm border-0">
      <div class="card-body py-4 text-center">
        <div class="spinner-border spinner-border-sm me-2"></div>
        Loading withdrawal detail...
      </div>
    </div>

    <ng-container *ngIf="!loading && item">
      <div class="card shadow-sm border-0 mb-3">
        <div class="card-body">
          <div class="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <div class="text-muted small">Withdrawal ID</div>
              <div class="fw-semibold">{{ item.withdrawalId }}</div>
            </div>

            <div>
              <div class="text-muted small">Status</div>
              <span class="badge rounded-pill" [ngClass]="badgeClass(item.status)">
                {{ item.status }}
              </span>
            </div>

            <div class="d-flex gap-2">
              <button
                *ngIf="canComplete()"
                class="btn btn-outline-success btn-sm"
                type="button"
                (click)="openCompleteModal()">
                Complete
              </button>

              <button
                *ngIf="canFail()"
                class="btn btn-outline-danger btn-sm"
                type="button"
                (click)="openFailModal()">
                Fail
              </button>

              <button
                *ngIf="canRetry()"
                class="btn btn-outline-primary btn-sm"
                type="button"
                (click)="retryRequest()"
                [disabled]="actionLoading">
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="row g-3">
        <div class="col-lg-6">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Summary</h6>

              <div class="row g-3">
                <div class="col-md-6">
                  <div class="text-muted small">User ID</div>
                  <div class="fw-semibold small">{{ item.userId }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Wallet ID</div>
                  <div class="fw-semibold small">{{ item.walletId }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Currency</div>
                  <div class="fw-semibold">{{ item.currency.code }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Amount</div>
                  <div class="fw-semibold">
                    {{ item.amount | smartAmount:item.currency.symbol }}
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">External Reference</div>
                  <div class="fw-semibold small">{{ item.externalReference }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Gateway Reference</div>
                  <div class="fw-semibold small">{{ item.gatewayReference || '-' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Payment Method</div>
                  <div class="fw-semibold">{{ item.paymentMethod || '-' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Gateway</div>
                  <div class="fw-semibold">{{ item.paymentGatewayName || item.gateway?.name || '-' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Wallet Hold ID</div>
                  <div class="fw-semibold small">{{ item.walletHoldId || '-' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Failure Reason</div>
                  <div class="fw-semibold text-danger small">{{ item.failureReason || '-' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Created</div>
                  <div class="fw-semibold">{{ item.createdAt | date:'medium' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Updated</div>
                  <div class="fw-semibold">{{ item.lastUpdatedAt | date:'medium' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Submitted</div>
                  <div class="fw-semibold">{{ item.submittedAt ? (item.submittedAt | date:'medium') : '-' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Completed</div>
                  <div class="fw-semibold">{{ item.completedAt ? (item.completedAt | date:'medium') : '-' }}</div>
                </div>

                <div class="col-md-6">
                  <div class="text-muted small">Failed</div>
                  <div class="fw-semibold">{{ item.failedAt ? (item.failedAt | date:'medium') : '-' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-6">
          <div class="card shadow-sm border-0 h-100">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Destination Details</h6>

              <div class="row g-3">
                <div class="col-md-6" *ngFor="let entry of destinationEntries()">
                  <div class="text-muted small">{{ pretty(entry.key) }}</div>
                  <div class="fw-semibold small">{{ entry.value }}</div>
                </div>
              </div>

              <div *ngIf="!destinationEntries().length" class="text-muted small">
                No destination details available.
              </div>
            </div>
          </div>
        </div>

        <div class="col-12">
          <div class="card shadow-sm border-0 mb-3">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Client Payload JSON</h6>
              <pre class="small mb-0">{{ item.clientPayloadJson || '-' }}</pre>
            </div>
          </div>

          <div class="card shadow-sm border-0 mb-3">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Gateway Init Request JSON</h6>
              <pre class="small mb-0">{{ item.gatewayInitRequestJson || '-' }}</pre>
            </div>
          </div>

          <div class="card shadow-sm border-0 mb-3">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Gateway Init Response JSON</h6>
              <pre class="small mb-0">{{ item.gatewayInitResponseJson || '-' }}</pre>
            </div>
          </div>

          <div class="card shadow-sm border-0">
            <div class="card-body">
              <h6 class="fw-bold mb-3">Gateway Verify Response JSON</h6>
              <pre class="small mb-0">{{ item.gatewayVerifyResponseJson || '-' }}</pre>
            </div>
          </div>
        </div>
      </div>
    </ng-container>

    <!-- Complete Modal -->
    <div
      class="modal fade show"
      tabindex="-1"
      [ngStyle]="{ display: showCompleteModal ? 'block' : 'none' }"
      [class.d-block]="showCompleteModal"
      *ngIf="showCompleteModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header">
            <h5 class="modal-title">Complete Withdrawal</h5>
            <button type="button" class="btn-close" (click)="closeModals()"></button>
          </div>

          <form [formGroup]="actionForm" (ngSubmit)="completeRequest()">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">Gateway Reference</label>
                <input type="text" class="form-control" formControlName="gatewayReference" />
              </div>

              <div class="mb-3">
                <label class="form-label">Provider Response JSON</label>
                <textarea class="form-control" rows="4" formControlName="providerResponseJson"></textarea>
              </div>
            </div>

            <div class="modal-footer">
              <button class="btn btn-outline-secondary" type="button" (click)="closeModals()">
                Cancel
              </button>

              <button class="btn btn-success" type="submit" [disabled]="actionLoading">
                <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
                Confirm Complete
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Fail Modal -->
    <div
      class="modal fade show"
      tabindex="-1"
      [ngStyle]="{ display: showFailModal ? 'block' : 'none' }"
      [class.d-block]="showFailModal"
      *ngIf="showFailModal">
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content border-0 shadow">
          <div class="modal-header">
            <h5 class="modal-title">Fail Withdrawal</h5>
            <button type="button" class="btn-close" (click)="closeModals()"></button>
          </div>

          <form [formGroup]="actionForm" (ngSubmit)="failRequest()">
            <div class="modal-body">
              <div class="mb-3">
                <label class="form-label">Failure Reason</label>
                <textarea class="form-control" rows="4" formControlName="failureReason"></textarea>
              </div>
            </div>

            <div class="modal-footer">
              <button class="btn btn-outline-secondary" type="button" (click)="closeModals()">
                Cancel
              </button>

              <button class="btn btn-danger" type="submit" [disabled]="actionLoading">
                <span *ngIf="actionLoading" class="spinner-border spinner-border-sm me-2"></span>
                Confirm Fail
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <div class="modal-backdrop fade show" *ngIf="showCompleteModal || showFailModal"></div>

    <app-footer></app-footer>
  </main>
</div>